import { WithTheme } from '@issuemanager/apps/client/theme';
import {
  Autocomplete,
  AutocompleteInputChangeReason,
  AutocompleteRenderGetTagProps,
  Box,
  Chip,
  CircularProgress,
  styled,
} from '@mui/material';
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Controller, FieldValues, UseControllerProps } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TextField, TextFieldProps } from '../text-field';

interface AutocompleteOption {
  id: string;
  label: string;
}

interface ExtendedControlledAutocompleteProps<TFieldValues extends FieldValues>
  extends UseControllerProps<TFieldValues> {
  label: string;
  labelInfo?: string;
  placeholder?: string;
  loading?: boolean;
  options: AutocompleteOption[];
  onTextChange?: (value: string, reason: AutocompleteInputChangeReason) => void;
  onChangeProp?: (option: AutocompleteOption | AutocompleteOption[]) => void;
  disabled?: boolean;
  readOnly?: boolean;
  multiple?: boolean;
  filterSelectedOptions?: boolean;
  disablePortal?: boolean;
  'data-testid'?: string;
}

const StyledTextField = styled(TextField)<WithTheme<TextFieldProps>>(
  ({ theme, inputProps }) => ({
    '.MuiInputBase-root': {
      backgroundColor: inputProps?.readOnly
        ? theme.tokens.semantic.color.background.disabled.value
        : theme.tokens.semantic.color.background.container.value,
    },
  }),
);

export function ExtendedControlledAutocomplete<
  TFieldValues extends FieldValues,
>({
  name,
  control,
  rules,
  label,
  labelInfo,
  placeholder,
  loading = false,
  options = [],
  onTextChange,
  onChangeProp,
  disabled = false,
  readOnly = false,
  multiple = false,
  disablePortal = true,
  filterSelectedOptions,
  ...props
}: ExtendedControlledAutocompleteProps<TFieldValues>): ReactNode {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const [isUserEditing, setIsUserEditing] = useState(false);
  const [controlledValue, setControlledValue] = useState<string>('');
  const lastSelectedValueRef = useRef<string>('');
  const formValueRef = useRef<string>('');
  const previousFormValueRef = useRef<string>('');

  useEffect(() => {
    // Detect form reset: if the form value changed but we didn't trigger it through user selection
    const isFormReset =
      previousFormValueRef.current !== formValueRef.current &&
      lastSelectedValueRef.current !== controlledValue;

    if (isFormReset) {
      setIsUserEditing(false);
    }

    if (
      isUserEditing &&
      !isFormReset &&
      controlledValue === lastSelectedValueRef.current
    ) {
      return;
    }

    if (multiple) {
      lastSelectedValueRef.current = controlledValue;
      previousFormValueRef.current = formValueRef.current;
      return;
    }

    const selectedOption = options.find(
      (option) => option.id === controlledValue,
    );
    if (selectedOption) {
      setInputValue(selectedOption.label);
    } else if (!controlledValue) {
      setInputValue('');
    }

    lastSelectedValueRef.current = controlledValue;
    previousFormValueRef.current = formValueRef.current;
  }, [controlledValue, options, isUserEditing, multiple]);

  useEffect(() => {
    if (lastSelectedValueRef.current !== controlledValue) {
      setIsUserEditing(false);
    }
  }, [controlledValue]);

  useLayoutEffect(() => {
    const newValue = formValueRef.current || '';
    if (controlledValue !== newValue) {
      setControlledValue(newValue);
    }
  });

  const handleInputChange = useCallback(
    (
      event: React.SyntheticEvent,
      value: string,
      reason: AutocompleteInputChangeReason,
    ) => {
      if (reason === 'reset') {
        if (event?.type === 'click') {
          setInputValue(value);
        }
        return;
      }

      if (reason === 'input') {
        setIsUserEditing(true);
      }

      setInputValue(value);
      onTextChange?.(value, reason);
    },
    [onTextChange],
  );

  const handleSelectionChange = useCallback(
    (
      _: React.SyntheticEvent,
      value: AutocompleteOption | AutocompleteOption[] | null,
      onChange: (val: any) => void,
    ) => {
      setIsUserEditing(false);

      onChangeProp?.(value as AutocompleteOption | AutocompleteOption[]);

      if (multiple) {
        onChange(value);
        return;
      }

      if (value && !Array.isArray(value)) {
        onChange(value.id);
      } else {
        onChange('');
      }
    },
    [onChangeProp, multiple],
  );

  const handleBlur = useCallback(() => {
    setIsUserEditing(false);

    if (multiple) {
      return;
    }

    if (controlledValue) {
      const selectedOption = options.find(
        (option) => option.id === controlledValue,
      );
      if (selectedOption) {
        setInputValue(selectedOption.label);
      }
    } else {
      const matchingOption = options.find(
        (option) => option.label.toLowerCase() === inputValue.toLowerCase(),
      );
      if (!matchingOption && inputValue.trim()) {
        setInputValue('');
        onTextChange?.('', 'input');
      }
    }
  }, [controlledValue, options, multiple, inputValue, onTextChange]);

  return (
    <Controller<TFieldValues>
      control={control}
      rules={rules}
      name={name}
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        formValueRef.current = value || '';

        const testId = props['data-testid'] ?? name;
        const currentValue = multiple
          ? value
          : options.find((option) => option.id === value) || null;

        return (
          <Autocomplete
            sx={{
              '&& .MuiAutocomplete-endAdornment': {
                display: 'flex',
                height: '100%',
                alignItems: 'center',
                top: 'auto',
                transform: 'none',
                pointerEvents: readOnly ? 'none' : 'auto',
                opacity: readOnly ? 0.5 : 1,
              },
            }}
            fullWidth
            multiple={multiple}
            options={options}
            noOptionsText={t('inputs.listBox.noOptions')}
            value={currentValue}
            inputValue={inputValue}
            loading={loading}
            disabled={disabled}
            readOnly={readOnly}
            disablePortal={disablePortal}
            filterSelectedOptions={filterSelectedOptions}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, val) => option.id === val.id}
            onInputChange={(e, newValue, reason) => {
              if (readOnly) return;
              handleInputChange(e, newValue, reason);
            }}
            onChange={(event, val) => {
              if (readOnly) return;
              handleSelectionChange(event, val, onChange);
            }}
            onOpen={(event) => {
              if (readOnly) {
                event.preventDefault();
              }
            }}
            onBlur={handleBlur}
            filterOptions={(x) => x}
            renderInput={(params) => (
              <StyledTextField
                {...params}
                aria-labelledby={`${name}-label`}
                data-testid={testId}
                label={label}
                labelInfo={labelInfo}
                required={!!rules?.required}
                error={!!error}
                name={name}
                helperText={error?.message}
                placeholder={placeholder}
                disabled={disabled}
                inputProps={{
                  ...params.inputProps,
                  readOnly,
                  'data-testid': `${testId}-input`,
                }}
              />
            )}
            loadingText={
              <Box textAlign='center'>
                <CircularProgress size={24} />
              </Box>
            }
            slotProps={{
              paper: {
                // we need this data-testid to be able to locate the list in the tests
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                'data-testid': 'autocomplete-ul',
              },
            }}
            renderTags={
              (
                val: AutocompleteOption[],
                getTagProps: AutocompleteRenderGetTagProps,
              ) =>
                val.map((option: AutocompleteOption, index: number) => (
                  <React.Fragment key={option.id}>
                    <Chip
                      variant='outlined'
                      label={option.label}
                      data-testid='chip'
                      {...getTagProps({ index })}
                    />
                    {index < val.length - 1 && ', '}
                  </React.Fragment>
                ))
              // eslint-disable-next-line react/jsx-curly-newline
            }
            {...props}
            data-testid={`${testId}-autocomplete`}
          />
        );
      }}
    />
  );
}
